import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { useWorkspace } from '../contexts/WorkspaceContext';
import api from '../lib/api';
import { 
  ChevronRight, ChevronDown, Plus, MoreHorizontal, FolderKanban,
  Circle, Calendar, User, Flag, Loader2, GripVertical,
  Trash2, Edit3, Users, MessageSquare, Sparkles, Send
} from 'lucide-react';
import { toast } from 'sonner';

// Inline Task Component
const InlineTask = ({ task, members, userRole, onUpdate, onDelete, onToggleComplete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(task.title);
  
  const canEdit = userRole !== 'viewer';
  const canDelete = userRole === 'owner' || userRole === 'admin';

  const handleSaveTitle = () => {
    if (editTitle.trim() && editTitle !== task.title) {
      onUpdate(task.task_id, { title: editTitle.trim() });
    }
    setIsEditing(false);
  };

  const assignedMember = members.find(m => m.user_id === task.assigned_to);

  return (
    <div className="group flex items-center gap-2 py-2 px-3 hover:bg-gray-50 rounded-lg transition-colors" data-testid={`task-row-${task.task_id}`}>
      <div className="opacity-0 group-hover:opacity-100 cursor-grab">
        <GripVertical className="w-4 h-4 text-gray-400" />
      </div>

      <Checkbox
        checked={task.completed}
        onCheckedChange={() => onToggleComplete(task.task_id)}
        className="data-[state=checked]:bg-[#4A9CC7] data-[state=checked]:border-[#4A9CC7]"
      />

      <div className="flex-1 min-w-0">
        {isEditing && canEdit ? (
          <Input
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            onBlur={handleSaveTitle}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSaveTitle();
              if (e.key === 'Escape') setIsEditing(false);
            }}
            autoFocus
            className="h-7 text-sm"
          />
        ) : (
          <span 
            className={`text-sm cursor-pointer ${task.completed ? 'line-through text-gray-400' : 'text-gray-800'}`}
            onClick={() => canEdit && setIsEditing(true)}
          >
            {task.title}
          </span>
        )}
      </div>

      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {assignedMember && (
          <Avatar className="w-5 h-5">
            <AvatarFallback className="text-[10px] bg-[#4A9CC7]/10">
              {assignedMember.name?.charAt(0)}
            </AvatarFallback>
          </Avatar>
        )}

        {task.due_date && (
          <span className="text-xs text-gray-500 flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        )}

        {task.priority && (
          <Badge className={`text-[10px] px-1.5 ${
            task.priority === 'high' ? 'bg-red-100 text-red-700' :
            task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
            'bg-green-100 text-green-700'
          }`}>
            {task.priority}
          </Badge>
        )}

        {canDelete && (
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-6 w-6 p-0 text-red-500"
            onClick={() => onDelete(task.task_id)}
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        )}
      </div>
    </div>
  );
};

// Project Section Component with AI Task Creation
const ProjectSection = ({ project, tasks, members, userRole, workspaceId, onTaskUpdate, onTaskDelete, onTaskComplete, onAddTask, onAIAddTask }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [isAIMode, setIsAIMode] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  
  const canEdit = userRole !== 'viewer';
  const projectTasks = tasks.filter(t => t.project_id === project.project_id);

  const handleAddTask = async () => {
    if (!newTaskTitle.trim()) return;
    
    if (isAIMode) {
      // AI-powered task creation
      setAiLoading(true);
      try {
        await onAIAddTask(project.project_id, newTaskTitle.trim());
        setNewTaskTitle('');
        setIsAddingTask(false);
        setIsAIMode(false);
      } finally {
        setAiLoading(false);
      }
    } else {
      await onAddTask(project.project_id, newTaskTitle.trim());
      setNewTaskTitle('');
      setIsAddingTask(false);
    }
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="mb-2">
      <div className="flex items-center gap-2 py-2 px-3 bg-gray-50 rounded-lg hover:bg-gray-100">
        <CollapsibleTrigger className="flex items-center gap-2 flex-1">
          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: project.color || '#4A9CC7' }} />
          <span className="font-medium">{project.name}</span>
          <span className="text-xs text-gray-400 ml-2">
            {projectTasks.filter(t => t.completed).length}/{projectTasks.length}
          </span>
        </CollapsibleTrigger>

        {canEdit && (
          <div className="flex gap-1">
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 w-7 p-0" 
              onClick={() => { setIsAddingTask(true); setIsAIMode(true); }}
              title="AI Quick Add"
            >
              <Sparkles className="w-4 h-4 text-[#4A9CC7]" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 w-7 p-0" 
              onClick={() => { setIsAddingTask(true); setIsAIMode(false); }}
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      <CollapsibleContent className="ml-4 mt-1">
        {isAddingTask && (
          <div className={`flex items-center gap-2 py-2 px-3 rounded-lg ${isAIMode ? 'bg-[#4A9CC7]/5 border border-[#4A9CC7]/20' : ''}`}>
            {isAIMode ? (
              <Sparkles className="w-4 h-4 text-[#4A9CC7]" />
            ) : (
              <Circle className="w-4 h-4 text-gray-300" />
            )}
            <Input
              value={newTaskTitle}
              onChange={(e) => setNewTaskTitle(e.target.value)}
              placeholder={isAIMode ? "Type naturally: 'Review docs tomorrow 3pm'..." : "Task name..."}
              autoFocus
              className="h-7 text-sm flex-1"
              disabled={aiLoading}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddTask();
                if (e.key === 'Escape') { setIsAddingTask(false); setIsAIMode(false); }
              }}
            />
            {aiLoading ? (
              <Loader2 className="w-4 h-4 animate-spin text-[#4A9CC7]" />
            ) : (
              <Button size="sm" className="h-7 bg-[#4A9CC7]" onClick={handleAddTask}>
                {isAIMode ? <Send className="w-3 h-3" /> : 'Add'}
              </Button>
            )}
          </div>
        )}

        {projectTasks.map(task => (
          <InlineTask
            key={task.task_id}
            task={task}
            members={members}
            userRole={userRole}
            onUpdate={onTaskUpdate}
            onDelete={onTaskDelete}
            onToggleComplete={onTaskComplete}
          />
        ))}

        {projectTasks.length === 0 && !isAddingTask && (
          <div className="py-4 text-center text-sm text-gray-400">
            No tasks yet
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};

// Main Workspace Canvas
const WorkspaceCanvas = () => {
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  
  const [workspaceData, setWorkspaceData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', color: '#4A9CC7' });
  const [isAddingDirectTask, setIsAddingDirectTask] = useState(false);
  const [directTaskTitle, setDirectTaskTitle] = useState('');

  const fetchWorkspaceData = useCallback(async () => {
    if (!currentWorkspace) return;
    
    setLoading(true);
    try {
      const [wsRes, tasksRes] = await Promise.all([
        api.get(`/workspaces/${currentWorkspace.workspace_id}`),
        api.get(`/workspaces/${currentWorkspace.workspace_id}/tasks`)
      ]);
      
      setWorkspaceData(wsRes.data);
      setProjects(wsRes.data.projects || []);
      setTasks(tasksRes.data.tasks || []);
    } catch (error) {
      console.error('Failed to fetch workspace data:', error);
      toast.error('Failed to load workspace');
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace]);

  useEffect(() => {
    fetchWorkspaceData();
  }, [fetchWorkspaceData]);

  const userRole = workspaceData?.user_role || 'viewer';
  const members = workspaceData?.members || [];
  const canEdit = userRole !== 'viewer';
  const canManage = userRole === 'owner' || userRole === 'admin';
  const directTasks = tasks.filter(t => !t.project_id);

  const handleTaskUpdate = async (taskId, updates) => {
    try {
      await api.patch(`/tasks/${taskId}`, updates);
      setTasks(prev => prev.map(t => t.task_id === taskId ? { ...t, ...updates } : t));
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleTaskDelete = async (taskId) => {
    try {
      await api.delete(`/tasks/${taskId}`);
      setTasks(prev => prev.filter(t => t.task_id !== taskId));
      toast.success('Task deleted');
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const handleTaskComplete = async (taskId) => {
    try {
      const task = tasks.find(t => t.task_id === taskId);
      const res = await api.post(`/tasks/${taskId}/complete`);
      if (res.data?.correction_task_created || (task?.is_recurring && !task?.completed)) {
        // Recurring task completed or correction task created — refresh the list
        const tasksRes = await api.get(`/tasks?workspace_id=${currentWorkspace.workspace_id}`);
        setTasks(tasksRes.data);
        toast.success(res.data?.correction_task_created 
          ? 'Correction task created for the same date.' 
          : 'Task completed! Next occurrence created.');
      } else {
        setTasks(prev => prev.map(t => t.task_id === taskId ? { ...t, completed: !t.completed } : t));
      }
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleAddTask = async (projectId, title) => {
    try {
      const response = await api.post('/tasks', {
        title,
        workspace_id: currentWorkspace.workspace_id,
        project_id: projectId,
        is_shared: true
      });
      setTasks(prev => [response.data, ...prev]);
      toast.success('Task added');
    } catch (error) {
      toast.error('Failed to add task');
    }
  };

  // AI-powered task creation
  const handleAIAddTask = async (projectId, naturalText) => {
    try {
      // Parse the natural language input with AI
      const parseResponse = await api.post('/ai/parse-task', { text: naturalText });
      const parsed = parseResponse.data;

      // Create the task with parsed data
      const taskData = {
        title: parsed.title || naturalText,
        description: parsed.description || null,
        due_date: parsed.due_date || null,
        due_time: parsed.due_time || null,
        priority: parsed.priority || 'medium',
        labels: parsed.labels || [],
        is_recurring: parsed.is_recurring || false,
        recurrence_rule: parsed.recurrence_rule || null,
        workspace_id: currentWorkspace.workspace_id,
        project_id: projectId,
        is_shared: true
      };

      const response = await api.post('/tasks', taskData);
      setTasks(prev => [response.data, ...prev]);
      toast.success(`AI created: ${parsed.title || naturalText}`);
    } catch (error) {
      console.error('AI Task creation error:', error);
      toast.error('Failed to create task');
    }
  };

  const handleAddDirectTask = async () => {
    if (!directTaskTitle.trim()) return;
    try {
      const response = await api.post('/tasks', {
        title: directTaskTitle.trim(),
        workspace_id: currentWorkspace.workspace_id,
        is_shared: true
      });
      setTasks(prev => [response.data, ...prev]);
      setDirectTaskTitle('');
      setIsAddingDirectTask(false);
      toast.success('Task added');
    } catch (error) {
      toast.error('Failed to add task');
    }
  };

  const handleCreateProject = async () => {
    if (!newProject.name.trim()) return;
    try {
      const response = await api.post('/projects', {
        name: newProject.name.trim(),
        workspace_id: currentWorkspace.workspace_id,
        color: newProject.color
      });
      setProjects(prev => [...prev, response.data]);
      setNewProject({ name: '', color: '#4A9CC7' });
      setShowCreateProject(false);
      toast.success('Project created');
    } catch (error) {
      toast.error('Failed to create project');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 text-[#4A9CC7] animate-spin" />
      </div>
    );
  }

  if (!currentWorkspace) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-500">
        <FolderKanban className="w-16 h-16 mb-4 text-gray-300" />
        <h3 className="text-lg font-medium">No Workspace Selected</h3>
        <p className="text-sm mt-1">Create or select a workspace to get started</p>
        <Button className="mt-4 bg-[#4A9CC7]" onClick={() => navigate('/workspaces')}>
          Go to Workspaces
        </Button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="workspace-canvas">
      {/* Header */}
      <div className="flex-shrink-0 border-b bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              currentWorkspace.workspace_type === 'team' ? 'bg-blue-100' : 'bg-gray-100'
            }`}>
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{currentWorkspace.name}</h1>
              <div className="flex items-center gap-3 text-sm text-gray-500">
                <span>{members.length} members</span>
                <span>{projects.length} projects</span>
                <Badge variant="outline" className="text-xs">{userRole}</Badge>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate(`/chat?workspace=${currentWorkspace.workspace_id}`)}>
              <MessageSquare className="w-4 h-4 mr-2" />Chat
            </Button>
            {canManage && (
              <Button size="sm" className="bg-[#4A9CC7]" onClick={() => setShowCreateProject(true)}>
                <Plus className="w-4 h-4 mr-2" />Project
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Canvas */}
      <div className="flex-1 overflow-auto p-6">
        {/* AI Banner */}
        <div className="mb-6 p-4 bg-gradient-to-r from-[#4A9CC7]/10 to-blue-50 rounded-xl border border-[#4A9CC7]/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#4A9CC7] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-800">AI Assistant</h3>
              <p className="text-sm text-gray-600">
                {tasks.filter(t => !t.completed && t.priority === 'high').length > 0
                  ? `You have ${tasks.filter(t => !t.completed && t.priority === 'high').length} high priority tasks`
                  : 'All caught up! Great job'}
              </p>
            </div>
            <Button variant="outline" size="sm">Plan My Day</Button>
          </div>
        </div>

        {/* Direct Tasks */}
        {(directTasks.length > 0 || isAddingDirectTask) && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2 px-3">
              <h3 className="text-sm font-medium text-gray-500 uppercase">Workspace Tasks</h3>
              {canEdit && !isAddingDirectTask && (
                <Button variant="ghost" size="sm" className="h-7" onClick={() => setIsAddingDirectTask(true)}>
                  <Plus className="w-4 h-4 mr-1" />Add Task
                </Button>
              )}
            </div>

            {isAddingDirectTask && (
              <div className="flex items-center gap-2 py-2 px-3 mb-2">
                <Circle className="w-4 h-4 text-gray-300" />
                <Input
                  value={directTaskTitle}
                  onChange={(e) => setDirectTaskTitle(e.target.value)}
                  placeholder="Task name..."
                  autoFocus
                  className="h-7 text-sm flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAddDirectTask();
                    if (e.key === 'Escape') setIsAddingDirectTask(false);
                  }}
                />
                <Button size="sm" className="h-7 bg-[#4A9CC7]" onClick={handleAddDirectTask}>Add</Button>
              </div>
            )}

            {directTasks.map(task => (
              <InlineTask
                key={task.task_id}
                task={task}
                members={members}
                userRole={userRole}
                onUpdate={handleTaskUpdate}
                onDelete={handleTaskDelete}
                onToggleComplete={handleTaskComplete}
              />
            ))}
          </div>
        )}

        {/* Projects */}
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-gray-500 uppercase mb-3 px-3">Projects</h3>

          {projects.map(project => (
            <ProjectSection
              key={project.project_id}
              project={project}
              tasks={tasks}
              members={members}
              userRole={userRole}
              workspaceId={currentWorkspace.workspace_id}
              onTaskUpdate={handleTaskUpdate}
              onTaskDelete={handleTaskDelete}
              onTaskComplete={handleTaskComplete}
              onAddTask={handleAddTask}
              onAIAddTask={handleAIAddTask}
            />
          ))}

          {projects.length === 0 && directTasks.length === 0 && (
            <div className="text-center py-12">
              <FolderKanban className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <h3 className="font-medium text-gray-700">Get Started</h3>
              <p className="text-sm text-gray-500 mt-1 mb-4">Create your first project or add tasks</p>
              <div className="flex justify-center gap-2">
                {canEdit && <Button variant="outline" onClick={() => setIsAddingDirectTask(true)}><Plus className="w-4 h-4 mr-2" />Add Task</Button>}
                {canManage && <Button className="bg-[#4A9CC7]" onClick={() => setShowCreateProject(true)}><Plus className="w-4 h-4 mr-2" />Create Project</Button>}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Project Dialog */}
      <Dialog open={showCreateProject} onOpenChange={setShowCreateProject}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Project Name</label>
              <Input
                value={newProject.name}
                onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                placeholder="e.g., Marketing Campaign"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Color</label>
              <div className="flex gap-2 mt-2">
                {['#4A9CC7', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'].map(color => (
                  <button
                    key={color}
                    className={`w-8 h-8 rounded-lg ${newProject.color === color ? 'ring-2 ring-offset-2 ring-gray-400' : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setNewProject({ ...newProject, color })}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button className="bg-[#4A9CC7]" onClick={handleCreateProject}>Create Project</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkspaceCanvas;
